import React, { useState } from 'react';
import {
    Button,
    FormField,
    Input,
    TokenGroup,
} from '@amzn/awsui-components-react';
import { FORM_ERROR_SELECTOR } from '../../../../../imt/components/Instructor/FormSections/FormSections.common';

interface EmailTokenInputProps {
    value: string;
    errorText?: string;
    onChange: (value: string) => void;
    testId?: string;
    label?: React.ReactNode;
    description?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseEmails = (csv: string): string[] =>
    (csv || '')
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);

const toCommaSeparated = (emails: string[]): string => emails.join(', ');

export const EmailTokenInput = ({
    value,
    errorText,
    onChange,
    testId = 'EditDeliveryContactEmail',
    label = (
        <span>
            Delivery contact email - <em>optional</em>
        </span>
    ),
    description = 'Enter an email address and click Add',
}: EmailTokenInputProps) => {
    const [inputValue, setInputValue] = useState('');
    const [inputError, setInputError] = useState('');
    const inputRef = React.useRef('');

    const emails = parseEmails(value);
    const tokens = emails.map((email) => ({
        label: email,
        dismissLabel: `Remove ${email}`,
    }));

    const addEmail = () => {
        const email = inputRef.current.trim();
        if (!email) return;
        if (!EMAIL_REGEX.test(email)) {
            setInputError('Enter a valid email address');
            return;
        }
        if (emails.some((e) => e.toLowerCase() === email.toLowerCase())) {
            setInputError('Email already added');
            return;
        }
        setInputError('');
        setInputValue('');
        inputRef.current = '';
        onChange(toCommaSeparated([...emails, email]));
    };

    const removeEmail = (index: number) => {
        const updated = emails.filter((_, i) => i !== index);
        onChange(toCommaSeparated(updated));
    };

    return (
        <FormField
            errorText={errorText || inputError}
            label={label}
            description={description}
        >
            <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                    <Input
                        className={
                            (errorText || inputError) && FORM_ERROR_SELECTOR
                        }
                        value={inputValue}
                        placeholder="e.g. name@example.com"
                        onChange={(e) => {
                            setInputValue(e.detail.value);
                            inputRef.current = e.detail.value;
                            setInputError('');
                        }}
                        onKeydown={(e) => {
                            if (e.detail.key === 'Enter') {
                                e.preventDefault();
                                addEmail();
                            }
                        }}
                        onBlur={() => {
                            if (inputRef.current.trim()) {
                                addEmail();
                            }
                        }}
                        data-testid={testId}
                    />
                </div>
                <Button onClick={addEmail} data-testid={`${testId}-add`}>
                    Add
                </Button>
            </div>
            {tokens.length > 0 && (
                <TokenGroup
                    items={tokens}
                    onDismiss={({ detail: { itemIndex } }) =>
                        removeEmail(itemIndex)
                    }
                    data-testid={`${testId}-tokens`}
                />
            )}
        </FormField>
    );
};
